import * as types from '@/store/mutation-types';
import { copyShaft } from '@/utils/utils';

const shaftFields = [
  '*',
  'type.*.*.*',
  'site_id.*',
  'site_id.company.*.*',
  'cover.*',
  'cone.*',
  'user_created.*',
  'user_created.user.*',
  'user_updated.*',
  'user_updated.user.*',
  'order_user.*',
  'order_user.user.*',
  'input_channel.*',
  'input_channel.diameter.*',
  'output_channel.*',
  'output_channel.diameter.*',
  'side_channels.*',
  'side_channels.diameter.*',
  'buildups.*',
  'buildups.type.*',
];

const getters = {
  latestShafts: state => state.latestShafts,
  currentShaft: state => state.currentShaft,
  selectChoices: state => state.selectChoices,
};

const actions = {
  async getLatestShafts({ commit, getters }) {
    try {
      if (!getters.isLoggedIn) return;
      const shafts = this._vm.$directus.items('shafts');
      const shaftsData = await shafts.readByQuery({
        sort: '-date_updated',
        fields: shaftFields,
        filter: {
          _or: [
            { user_updated: { _eq: getters.user.id } },
            { user_created: { _eq: getters.user.id } },
          ],
        },
        limit: 5,
      });
      commit(types.SAVE_SHAFTS, shaftsData.data);
    } catch (error) {
      console.log('Error', error, error.message);
      commit(types.SHOW_ERROR, error.message);
    }
  },
  async addShaft({ commit, getters, dispatch }, payload) {
    try {
      const shafts = this._vm.$directus.items('shafts');

      var shaft = payload;
      // if copyFrom is set, we take all fields of this shaft
      // except title and site_id
      var copyWithBuildup = false;
      if (payload.copyFrom) {
        shaft = payload.copyFrom;
        shaft.title = payload.title;
        shaft.site_id = payload.site_id;
        copyWithBuildup = payload.copyWithBuildup;
      }
      var newShaft = copyShaft(shaft, false, true, copyWithBuildup);
      var result = await shafts.createOne(newShaft);
      newShaft.site_id = getters.sites.find(site => site.id === result.site_id);
      newShaft.id = result.id;
      commit(types.SAVE_SHAFT, undefined); // force reload data when opening shaft

      dispatch('getSites');
      dispatch('getLatestShafts');

      return result;
    } catch (error) {
      //console.error(error);
      commit(types.SHOW_ERROR, error.message);
      throw error;
    }
  },
  async deleteShaft({ commit, dispatch }, payload) {
    try {
      const shafts = this._vm.$directus.items('shafts');
      await shafts.deleteOne(payload.id);
      dispatch('getSites');
      dispatch('getLatestShafts');
    } catch (error) {
      //console.error(error);
      commit(types.SHOW_ERROR, error.message);
      throw error;
    }
  },
  async setCurrentShaft({ commit }, payload) {
    commit(types.SAVE_SHAFT, payload);
  },
  async updateCurrentShaft({ commit, getters }, payload) {
    try {
      const shafts = this._vm.$directus.items('shafts');
      var newShaft = copyShaft(payload, true, true, true);
      var result = await shafts.updateOne(payload.id, newShaft);
      payload.user_updated = getters.user;
      //console.log(payload);
      commit(types.SAVE_SHAFT, payload);
      return result;
    } catch (error) {
      //console.error(error);
      commit(types.SHOW_ERROR, error.message);
      throw error;
    }
  },
  async getCurrentShaft({ commit }, payload) {
    try {
      const shafts = this._vm.$directus.items('shafts');
      var shaft = await shafts.readOne(payload, {
        fields: shaftFields,
      });
      commit(types.SAVE_SHAFT, shaft);
    } catch (error) {
      //console.error(error);
      commit(types.SHOW_ERROR, error.message);
    }
  },
  async getSelectChoices({ commit }) {
    try {
      const choices = await this._vm.$directus.transport.get(
        '/shafts/dropdown-choices',
      );

      let options = this._vm.$directus.items('shaft_options_base');
      var optionsResults = await options.readByQuery({
        fields: [
          '*',
          'types.shaft_options_base_types_id.*',
          'channel_options.*',
        ],
        filter: { status: { _eq: 'published' } },
      });
      choices.raw.push({
        field: 'type',
        collection: 'shaft_options_base',
        interface: 'relational',
        options: { choices: optionsResults.data },
      });

      options = this._vm.$directus.items('shaft_options_base_types');
      optionsResults = await options.readByQuery({
        filter: { status: { _eq: 'published' } },
      });
      choices.raw.push({
        field: 'subtype',
        collection: 'shaft_options_base_types',
        interface: 'relational',
        options: { choices: optionsResults.data },
      });

      options = this._vm.$directus.items('shaft_options_cover');
      optionsResults = await options.readByQuery({
        filter: { status: { _eq: 'published' } },
      });
      choices.raw.push({
        field: 'cover',
        collection: 'shaft_options_cover',
        interface: 'relational',
        options: { choices: optionsResults.data },
      });

      options = this._vm.$directus.items('shaft_options_buildup');
      optionsResults = await options.readByQuery({
        filter: { status: { _eq: 'published' } },
      });
      choices.raw.push({
        field: 'buildup',
        collection: 'shaft_options_buildup',
        interface: 'relational',
        options: { choices: optionsResults.data },
      });

      options = this._vm.$directus.items('shaft_options_materials');
      optionsResults = await options.readByQuery({
        filter: { status: { _eq: 'published' } },
      });
      choices.raw.push({
        field: 'material',
        collection: 'shaft_options_materials',
        interface: 'relational',
        options: { choices: optionsResults.data },
      });

      commit(types.SAVE_SELECT_CHOICES, choices.raw);
    } catch (error) {
      //console.error(error);
      commit(types.SHOW_ERROR, error.message);
      throw error;
    }
  },
  async createShaftBuildup({ commit }, payload) {
    try {
      const shaftWithBuildup = await this._vm.$directus.transport.post(
        '/shafts/create-buildup',
        payload,
      );
      commit(types.SAVE_SHAFT, shaftWithBuildup.raw);
    } catch (error) {
      //console.error(error);
      commit(types.SHOW_ERROR, error.message);
      throw error;
    }
  },
  async resetShaftBase({ commit }, payload) {
    try {
      const shafts = this._vm.$directus.items('shafts');
      var newShaft = copyShaft(payload, true, false, true);
      var result = await shafts.updateOne(payload.id, newShaft, {
        fields: shaftFields,
      });
      commit(types.SAVE_SHAFT, result);
      return result;
    } catch (error) {
      //console.error(error);
      commit(types.SHOW_ERROR, error.message);
      throw error;
    }
  },
  async resetShaftBuildup({ commit }, payload) {
    try {
      const shafts = this._vm.$directus.items('shafts');
      var newShaft = copyShaft(payload, true, true, false);
      var result = await shafts.updateOne(payload.id, newShaft, {
        fields: shaftFields,
      });
      commit(types.SAVE_SHAFT, result);
      return result;
    } catch (error) {
      //console.error(error);
      commit(types.SHOW_ERROR, error.message);
      throw error;
    }
  },
};

const mutations = {
  [types.SAVE_SHAFTS](state, latestShafts) {
    state.latestShafts = latestShafts;
  },
  [types.SAVE_SHAFT](state, currentShaft) {
    state.currentShaft = currentShaft;
  },
  [types.SAVE_SELECT_CHOICES](state, selectChoices) {
    state.selectChoices = selectChoices;
  },
};

const state = {
  latestShafts: [],
  currentShaft: undefined,
  selectChoices: [],
};

export default {
  state,
  getters,
  actions,
  mutations,
};
